import React from 'react';
import { Activity, Route, FileText, TrendingDown, Wrench, Zap } from 'lucide-react';
import { siteConfig } from '../data/mock';

const FrotasSection = () => {
  const whatsappLink = `https://goprotege.com/frota/`;

  const frotasFeatures = [
    {
      icon: Activity,
      title: 'Monitoramento em Tempo Real',
      description: 'Visualize todos os veículos da sua frota em um único painel. Acompanhe posição, velocidade e status em tempo real.',
      bgColor: 'bg-gray-100'
    },
    {
      icon: Route,
      title: 'Histórico de Rotas (3 meses)',
      description: 'Acesse o histórico completo de rotas percorridas por até 3 meses. Ideal para auditorias e comprovação de entregas.',
      bgColor: 'bg-gray-100'
    },
    {
      icon: FileText,
      title: 'Relatórios Completos',
      description: 'Relatórios detalhados de percurso, jornada, paradas, excesso de velocidade e muito mais. Exportáveis em PDF ou Excel.',
      bgColor: 'bg-gray-100'
    },
    {
      icon: TrendingDown,
      title: 'Redução de Custos Operacionais',
      description: 'Empresas em Sorocaba reduzem em média 20% os custos com combustível, manutenção e horas extras com nossa gestão de frotas.',
      bgColor: 'bg-blue-50'
    },
    {
      icon: Wrench,
      title: 'Gestão de Manutenção',
      description: 'Sistema de alertas preventivos para manutenção programada. Evite paradas inesperadas e prolongue a vida útil da frota.',
      bgColor: 'bg-gray-100'
    },
    {
      icon: Zap,
      title: 'Telemetria Avançada',
      description: 'Dados de comportamento do motorista: frenagens bruscas, acelerações abruptas, curvas em alta velocidade e mais.',
      bgColor: 'bg-gray-100'
    }
  ];

  return (
    <section id="frotas" className="bg-white py-20">
      <div className="container mx-auto px-6 md:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-block bg-blue-100 rounded-lg px-6 py-2 mb-6">
              <span className="text-blue-600 text-sm font-bold uppercase tracking-wider">Gestão de Frotas</span>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-start mb-12">
            {/* Left - Image */}
            <div className="space-y-10">
              <div className="relative">
                <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                  <img
                    src="https://images.unsplash.com/photo-1528457616777-84ce44cc3699?w=800&q=80"
                    alt="Rastreamento de frotas em Sorocaba"
                    className="w-full h-[550px] object-cover"
                  />
                  
                  {/* Badge with discount - top right of image */}
                  <div className="absolute top-8 right-8 bg-blue-600 rounded-2xl px-8 py-6 shadow-2xl">
                    <div className="text-white text-5xl font-bold mb-1">-20%</div>
                    <div className="text-white text-sm font-medium">Redução de custos</div>
                  </div>

                  {/* Status card at bottom */}
                  <div className="absolute bottom-6 left-6 right-6 bg-[#1a1f2e]/95 backdrop-blur-sm rounded-2xl px-6 py-4 border border-white/10">
                    <div className="flex items-center gap-3">
                      <div className="w-2.5 h-2.5 bg-green-500 rounded-full flex-shrink-0"></div>
                      <div>
                        <p className="text-white font-semibold text-base">Frota monitorada — Sorocaba/SP</p>
                        <p className="text-gray-400 text-sm">Rastreamento ativo agora</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* CTA Button below image */}
              <div>
                <a
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-gray-900 hover:bg-gray-800 text-white px-8 py-4 rounded-2xl font-bold text-base transition-all shadow-xl hover:scale-105 w-full justify-center"
                >
                  <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 12l2 2 4-4" />
                  </svg>
                  Ver Soluções para Frotas em Sorocaba
                </a>
              </div>
            </div>

            {/* Right - Content */}
            <div className="space-y-8">
              <div>
                <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
                  Rastreamento para Frotas <span className="text-blue-600">em Sorocaba</span>
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Reduza custos operacionais, aumente a produtividade e tenha controle total da sua frota com o sistema de{' '}
                  <strong className="text-gray-900">rastreamento automotivo em Sorocaba</strong> mais completo do mercado.
                </p>
              </div>

              {/* Features Grid - 3x2 */}
              <div className="grid grid-cols-2 gap-4">
                {frotasFeatures.map((feature, index) => {
                  const Icon = feature.icon;
                  return (
                    <div
                      key={index}
                      className={`${feature.bgColor} rounded-2xl p-5 hover:shadow-md transition-all`}
                    >
                      <div className="flex items-start gap-3 mb-3">
                        <div className="w-12 h-12 bg-gray-900 rounded-xl flex items-center justify-center flex-shrink-0">
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        <h3 className="text-base font-bold text-gray-900 leading-tight pt-2">{feature.title}</h3>
                      </div>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FrotasSection;
